/**
 * Migration Validation Script
 * 
 * Comprehensive validation to ensure migration was successful and data integrity
 * is maintained between legacy and new schema
 */

import { API, graphqlOperation } from 'aws-amplify';

// Import queries for both legacy and new schema
import { listAreas2 } from 'graphql/queries';
import { 
  orgUnitsByClientAndLevel,
  storesByClientSorted,
  // managerAssignmentsByClient // REMOVED: ManagerAssignmentHistory removed from schema
} from 'graphql/queries';

class MigrationValidator {
  constructor(clientId = 'grm') {
    this.clientId = clientId;
    this.validationResults = {
      summary: {
        isValid: false,
        totalChecks: 0,
        passedChecks: 0,
        failedChecks: 0
      },
      checks: []
    };
  }

  /**
   * Run comprehensive validation
   */
  async validate() {
    console.log(`🔍 Starting migration validation for client: ${this.clientId}`);
    
    try {
      // Fetch all data
      const legacyData = await this.fetchLegacyData();
      const newData = await this.fetchNewSchemaData();

      // Run validation checks
      await this.validateAreaMigration(legacyData, newData);
      await this.validateStoreMigration(legacyData, newData);
      await this.validateManagerMigration(legacyData, newData);
      await this.validateDataIntegrity(legacyData, newData);
      await this.validateHierarchyStructure(newData);

      // Calculate final results
      this.calculateSummary();
      this.printResults();

      return this.validationResults;

    } catch (error) {
      console.error('❌ Validation failed:', error);
      throw error;
    }
  }

  /**
   * Fetch legacy data
   */
  async fetchLegacyData() {
    try {
      const result = await API.graphql(graphqlOperation(listAreas2));
      const allAreas = result.data.listAreas2 || [];
      
      // Filter out Ghost Kitchen area (same as migration)
      const filteredAreas = allAreas.filter(area => area.areaName !== 'Ghost Kitchen');
      const skippedAreas = allAreas.filter(area => area.areaName === 'Ghost Kitchen');
      
      if (skippedAreas.length > 0) {
        console.log(`📊 Validation excludes Ghost Kitchen: ${skippedAreas.length} areas, ${skippedAreas.reduce((sum, area) => sum + area.stores.length, 0)} stores`);
      }
      
      return filteredAreas;
    } catch (error) {
      console.error('Failed to fetch legacy data:', error);
      throw error;
    }
  }

  /**
   * Fetch new schema data
   */
  async fetchNewSchemaData() {
    const data = {};

    try {
      // OrgUnits (Areas)
      const orgUnitsResult = await API.graphql(
        graphqlOperation(orgUnitsByClientAndLevel, { 
          clientId: this.clientId, 
          orgLevel: "ORGLEVEL_1" 
        })
      );
      data.orgUnits = orgUnitsResult.data.orgUnitsByClientAndLevel?.items || [];

      // Stores
      const storesResult = await API.graphql(
        graphqlOperation(storesByClientSorted, { 
          clientId: this.clientId 
        })
      );
      data.stores = storesResult.data.storesByClientSorted?.items || [];

      // REMOVED: ManagerAssignmentHistory removed from schema
      // Will be replaced with LocationEmployee and OrgUnitEmployee queries
      // // Manager Assignments
      // const managersResult = await API.graphql(
      //   graphqlOperation(managerAssignmentsByClient, { 
      //     clientId: this.clientId 
      //   })
      // );
      // data.managers = managersResult.data.managerAssignmentsByClient?.items || [];
      data.managers = []; // Temporary until Employee queries are implemented

      return data;

    } catch (error) {
      console.error('Failed to fetch new schema data:', error);
      throw error;
    }
  }

  /**
   * Validate area migration
   */
  async validateAreaMigration(legacyData, newData) {
    console.log('🔍 Validating area migration...');

    // Check count
    const expectedCount = legacyData.length;
    const actualCount = newData.orgUnits.length;
    
    this.addCheck(
      'Area Count Match',
      actualCount === expectedCount,
      `Expected ${expectedCount}, got ${actualCount}`
    );

    // Check each area exists
    for (const legacyArea of legacyData) {
      const newOrgUnit = newData.orgUnits.find(unit => unit.id === legacyArea.areaId);
      
      this.addCheck(
        `Area Exists: ${legacyArea.areaName}`,
        !!newOrgUnit,
        newOrgUnit ? 'Found' : 'Missing'
      );

      if (newOrgUnit) {
        // Check name preservation
        this.addCheck(
          `Area Name: ${legacyArea.areaName}`,
          newOrgUnit.name === legacyArea.areaName,
          `Expected "${legacyArea.areaName}", got "${newOrgUnit.name}"`
        );

        // Check store count
        this.addCheck(
          `Store Count: ${legacyArea.areaName}`,
          newOrgUnit.storeCount === legacyArea.stores.length,
          `Expected ${legacyArea.stores.length}, got ${newOrgUnit.storeCount}`
        );

        // Check metadata preservation
        const metadata = JSON.parse(newOrgUnit.metadata || '{}');
        this.addCheck(
          `Metadata: ${legacyArea.areaName}`,
          metadata.areaType === legacyArea.areaType,
          `Area type preserved: ${metadata.areaType}`
        );
      }
    }
  }

  /**
   * Validate store migration
   */
  async validateStoreMigration(legacyData, newData) {
    console.log('🔍 Validating store migration...');

    // Calculate expected store count
    const expectedStoreCount = legacyData.reduce((sum, area) => sum + area.stores.length, 0);
    const actualStoreCount = newData.stores.length;

    this.addCheck(
      'Store Count Match',
      actualStoreCount === expectedStoreCount,
      `Expected ${expectedStoreCount}, got ${actualStoreCount}`
    );

    // Check compStores usage across all stores
    let compStoresUsageCount = 0;
    const compStoresExamples = [];

    // Check each store
    for (const legacyArea of legacyData) {
      for (const legacyStore of legacyArea.stores) {
        // Check compStores usage
        if (legacyStore.compStores && Array.isArray(legacyStore.compStores) && legacyStore.compStores.length > 0) {
          compStoresUsageCount++;
          compStoresExamples.push({
            store: legacyStore.shortName,
            compStores: legacyStore.compStores
          });
        }

        const newStore = newData.stores.find(store => store.id === legacyStore.storeId);
        
        this.addCheck(
          `Store Exists: ${legacyStore.shortName}`,
          !!newStore,
          newStore ? 'Found' : 'Missing'
        );

        if (newStore) {
          // Check hierarchy assignment
          this.addCheck(
            `Store Area Assignment: ${legacyStore.shortName}`,
            newStore.level_1_id === legacyArea.areaId,
            `Assigned to ${newStore.level_1_name || 'none'}`
          );

          // Check name preservation
          const expectedName = legacyStore.longName || legacyStore.shortName;
          this.addCheck(
            `Store Name: ${legacyStore.shortName}`,
            newStore.storeName === expectedName,
            `Expected "${expectedName}", got "${newStore.storeName}"`
          );

          // Check ownership type mapping
          const expectedOwnershipType = this.mapLegacyTypeToOwnership(legacyStore.type);
          this.addCheck(
            `Ownership Type: ${legacyStore.shortName}`,
            newStore.ownershipType === expectedOwnershipType,
            `Expected "${expectedOwnershipType}", got "${newStore.ownershipType}"`
          );

          // Check that date fields are left null for client to fill
          this.addCheck(
            `Open Date Left Null: ${legacyStore.shortName}`,
            !newStore.openDate,
            newStore.openDate ? 'Open date should be null for client to fill' : 'Correctly left null'
          );

          this.addCheck(
            `Close Date Preserved: ${legacyStore.shortName}`,
            true, // Always pass - we don't modify existing closeDate
            newStore.closeDate ? `Existing close date preserved: ${newStore.closeDate}` : 'No existing close date (correctly unchanged)'
          );

          // Check isActive logic (only based on legacy status)
          const expectedActive = legacyStore.status !== "Closed";
          this.addCheck(
            `Active Status: ${legacyStore.shortName}`,
            newStore.isActive === expectedActive,
            `Status "${legacyStore.status}" -> isActive: ${newStore.isActive}`
          );

          // Check metadata preservation
          const metadata = JSON.parse(newStore.metadata || '{}');
          this.addCheck(
            `Store Operational Data: ${legacyStore.shortName}`,
            !!metadata.fohBaseTheoHrs && !!metadata.bohBaseTheoHrs,
            'Theoretical hours preserved'
          );

          this.addCheck(
            `Store Thresholds: ${legacyStore.shortName}`,
            !!metadata.thresholds,
            'Thresholds preserved'
          );

          // Check legacy data preservation
          this.addCheck(
            `Legacy Status Reference: ${legacyStore.shortName}`,
            metadata.legacyStatus === legacyStore.status,
            `Legacy status "${legacyStore.status}" preserved in metadata`
          );

          // Check Toast ID preservation in metadata
          if (legacyStore.toastId) {
            this.addCheck(
              `Toast ID in Metadata: ${legacyStore.shortName}`,
              metadata.toastId === legacyStore.toastId,
              metadata.toastId ? `Toast ID preserved: ${metadata.toastId}` : 'Toast ID missing from metadata'
            );
          }
        }
      }
    }

    // Report on compStores usage
    this.addCheck(
      'CompStores Usage Analysis',
      true, // Always pass - this is informational
      compStoresUsageCount === 0 
        ? 'No stores use compStores field - safe to exclude from new schema'
        : `${compStoresUsageCount} stores use compStores field - review examples: ${JSON.stringify(compStoresExamples.slice(0, 3))}`
    );
  }

  /**
   * Helper: Map legacy type to ownership type for validation
   */
  mapLegacyTypeToOwnership(legacyType) {
    switch (legacyType) {
      case 'B&M':
        return 'Company';
      case 'Franchise':
        return 'Franchise';
      default:
        return 'Company'; // Default fallback
    }
  }

  /**
   * Validate manager migration
   */
  async validateManagerMigration(legacyData, newData) {
    console.log('🔍 Validating manager migration...');

    // Collect unique managers from legacy data
    const legacyManagers = new Set();
    const managerAreaMap = new Map();

    for (const area of legacyData) {
      for (const store of area.stores) {
        if (store.gm && Array.isArray(store.gm)) {
          for (const managerId of store.gm) {
            legacyManagers.add(managerId);
            if (!managerAreaMap.has(managerId)) {
              managerAreaMap.set(managerId, new Set());
            }
            managerAreaMap.get(managerId).add(area.areaId);
          }
        }
      }
    }

    // Check manager count
    this.addCheck(
      'Manager Count',
      newData.managers.length >= legacyManagers.size,
      `Expected at least ${legacyManagers.size}, got ${newData.managers.length}`
    );

    // Check each manager
    for (const managerId of legacyManagers) {
      const managerAssignment = newData.managers.find(m => m.userId === managerId);
      
      this.addCheck(
        `Manager Assignment: ${managerId}`,
        !!managerAssignment,
        managerAssignment ? 'Assignment created' : 'Assignment missing'
      );

      if (managerAssignment) {
        const expectedAreas = managerAreaMap.get(managerId);
        const assignedAreas = new Set(managerAssignment.managedOrgUnitIds);
        
        this.addCheck(
          `Manager Territory: ${managerId}`,
          this.setsEqual(expectedAreas, assignedAreas),
          `Expected ${expectedAreas.size} areas, assigned ${assignedAreas.size} areas`
        );
      }
    }
  }

  /**
   * Validate data integrity
   */
  async validateDataIntegrity(legacyData, newData) {
    console.log('🔍 Validating data integrity...');

    // Check for orphaned stores (stores without valid area assignment)
    const validAreaIds = new Set(newData.orgUnits.map(unit => unit.id));
    const orphanedStores = newData.stores.filter(store => 
      store.level_1_id && !validAreaIds.has(store.level_1_id)
    );

    this.addCheck(
      'No Orphaned Stores',
      orphanedStores.length === 0,
      orphanedStores.length > 0 ? `${orphanedStores.length} orphaned stores` : 'All stores properly assigned'
    );

    // Check for missing store assignments
    const unassignedStores = newData.stores.filter(store => !store.level_1_id);
    
    this.addCheck(
      'All Stores Assigned',
      unassignedStores.length === 0,
      unassignedStores.length > 0 ? `${unassignedStores.length} unassigned stores` : 'All stores assigned to areas'
    );

    // Check metadata completeness
    const storesWithoutMetadata = newData.stores.filter(store => {
      try {
        const metadata = JSON.parse(store.metadata || '{}');
        return !metadata.fohBaseTheoHrs || !metadata.bohBaseTheoHrs;
      } catch {
        return true;
      }
    });

    this.addCheck(
      'Store Metadata Complete',
      storesWithoutMetadata.length === 0,
      storesWithoutMetadata.length > 0 ? `${storesWithoutMetadata.length} stores missing metadata` : 'All stores have complete metadata'
    );
  }

  /**
   * Validate hierarchy structure
   */
  async validateHierarchyStructure(newData) {
    console.log('🔍 Validating hierarchy structure...');

    // Check that all org units are ORGLEVEL_1
    const incorrectLevels = newData.orgUnits.filter(unit => unit.orgLevel !== 'ORGLEVEL_1');
    
    this.addCheck(
      'Correct Org Levels',
      incorrectLevels.length === 0,
      incorrectLevels.length > 0 ? `${incorrectLevels.length} units with incorrect level` : 'All org units at correct level'
    );

    // Check client isolation
    const wrongClientRecords = [
      ...newData.orgUnits.filter(unit => unit.clientId !== this.clientId),
      ...newData.stores.filter(store => store.clientId !== this.clientId),
      ...newData.managers.filter(manager => manager.clientId !== this.clientId)
    ];

    this.addCheck(
      'Client Isolation',
      wrongClientRecords.length === 0,
      wrongClientRecords.length > 0 ? `${wrongClientRecords.length} records with wrong clientId` : 'All records properly isolated'
    );
  }

  /**
   * Helper methods
   */
  addCheck(name, passed, details) {
    this.validationResults.checks.push({
      name,
      passed,
      details,
      timestamp: new Date().toISOString()
    });
    this.validationResults.summary.totalChecks++;
    if (passed) {
      this.validationResults.summary.passedChecks++;
    } else {
      this.validationResults.summary.failedChecks++;
    }
  }

  setsEqual(set1, set2) {
    return set1.size === set2.size && [...set1].every(item => set2.has(item));
  }

  calculateSummary() {
    this.validationResults.summary.isValid = this.validationResults.summary.failedChecks === 0;
  }

  printResults() {
    console.log('\n📊 VALIDATION RESULTS');
    console.log('='.repeat(50));
    
    const { summary } = this.validationResults;
    console.log(`✅ Passed: ${summary.passedChecks}`);
    console.log(`❌ Failed: ${summary.failedChecks}`);
    console.log(`📊 Total:  ${summary.totalChecks}`);
    console.log(`🎯 Valid:  ${summary.isValid ? 'YES' : 'NO'}`);
    
    if (summary.failedChecks > 0) {
      console.log('\n❌ FAILED CHECKS:');
      this.validationResults.checks
        .filter(check => !check.passed)
        .forEach(check => {
          console.log(`   - ${check.name}: ${check.details}`);
        });
    }

    console.log('\n' + '='.repeat(50));
    
    if (summary.isValid) {
      console.log('🎉 MIGRATION VALIDATION SUCCESSFUL!');
    } else {
      console.log('⚠️  MIGRATION VALIDATION FAILED - REVIEW ERRORS ABOVE');
    }
  }
}

// Usage function
export async function validateMigration(clientId = 'grm') {
  const validator = new MigrationValidator(clientId);
  return await validator.validate();
}

// Export for testing
export { MigrationValidator };

/**
 * Example usage:
 * 
 * import { validateMigration } from './scripts/validateMigration.js';
 * 
 * // Validate migration
 * const results = await validateMigration('grm');
 * 
 * if (results.summary.isValid) {
 *   console.log('Migration is valid!');
 * } else {
 *   console.log('Migration has issues:', results.summary.failedChecks);
 * }
 */